import { ProductFlow } from 'data/__generated__';

import { PRODUCT_FLOW_LABELS } from 'pages/Products/constants/flows';

export type MinimumOrderAmountInfo = {
    minimumBakeryAmount: number;
    minimumCatererAmount: number;
    minimumDryAmount: number;
    minimumFreshAmount: number;
    minimumFruitAmount: number;
    minimumTotalAmount: number;
};

export type CartLineForMinimums = {
    flow: ProductFlow;
    quantity: number;
    unitPrice: number;
};

export type MinimumProgress = {
    label: string;
    amount: number;
    minimum: number;
    isReached: boolean;
};

const MINIMUM_FIELD_BY_FLOW: Partial<Record<ProductFlow, keyof MinimumOrderAmountInfo>> = {
    [ProductFlow.Bakery]: 'minimumBakeryAmount',
    [ProductFlow.Caterer]: 'minimumCatererAmount',
    [ProductFlow.Dry]: 'minimumDryAmount',
    [ProductFlow.Fresh]: 'minimumFreshAmount',
    [ProductFlow.Fruit]: 'minimumFruitAmount',
};

/**
 * Les minimums de commande, tels que l'API les appliquera à la validation
 * (`Order.isOrderPriceTooLow`) : le total s'applique toujours, un minimum par catégorie seulement
 * quand cette catégorie contient quelque chose.
 *
 * Le total est comparé au sous-total produits, pas au `priceHT` — celui-ci inclut la livraison et la
 * remise, qu'on ne connaît pas ici. L'écart est signalé par l'API dans ses `warnings`.
 */
export function computeMinimums(
    lines: readonly CartLineForMinimums[],
    minimumOrderAmountInfo: MinimumOrderAmountInfo,
): { total: MinimumProgress; byFlow: MinimumProgress[] } {
    const amountByFlow = lines.reduce<Partial<Record<ProductFlow, number>>>((acc, { flow, quantity, unitPrice }) => {
        acc[flow] = (acc[flow] ?? 0) + quantity * unitPrice;
        return acc;
    }, {});

    const totalAmount = Object.values(amountByFlow).reduce<number>((sum, amount) => sum + (amount ?? 0), 0);

    const byFlow = Object.entries(MINIMUM_FIELD_BY_FLOW).flatMap(([flow, field]) => {
        const amount = amountByFlow[flow as ProductFlow] ?? 0;
        const minimum = minimumOrderAmountInfo[field];

        if (!amount || !minimum) {
            return [];
        }

        return [
            {
                label: PRODUCT_FLOW_LABELS[flow as ProductFlow],
                amount,
                minimum,
                isReached: amount >= minimum,
            },
        ];
    });

    return {
        total: {
            label: 'Total',
            amount: totalAmount,
            minimum: minimumOrderAmountInfo.minimumTotalAmount,
            isReached: totalAmount >= minimumOrderAmountInfo.minimumTotalAmount,
        },
        byFlow,
    };
}
